/*
 * Invitations area (audit Phase 3.3). Replaces wwwroot/js/Invite/List.js on
 * the Phase 3.1 core. No jQuery.
 *
 * The legacy file only knew resend and cancel, because issuing an invite was a
 * different screen entirely -- a full-page form on PersonController that posted
 * as ordinary HTML and answered on its own "invite sent" page, navigating the
 * browser out of the SPA shell to do it. Issue lives here now, as a modal on
 * the pending list, so all three verbs act on the rows in front of the
 * operator and each one leaves them looking at the result.
 */
import { ajaxJson, ajaxModal, ajaxSwap } from '../core/ajax';
import { all, one } from '../core/dom';
import { serialize } from '../core/forms';
import { modelStateSummary } from '../core/http';
import { confirm, hideModal } from '../core/modal';
import { toast } from '../core/toast';
import { isValid, setValidationModelState } from '../core/validation';

const MODAL = 'InviteModal';
const FORM = 'IssueInviteForm';
const ALERT_INTO = 'InvitePendingMessageContainer';

function setHidden(selector: string, hidden: boolean): void {
    for (const node of all(selector)) node.classList.toggle('d-none', hidden);
}

function summaryOf(data: unknown): string {
    const modelState = (data as { modelState?: unknown } | null)?.modelState;
    return modelState ? modelStateSummary(modelState) : '';
}

/**
 * The domain the screen was rendered for. #InviteDomainId is written by
 * Index.cshtml; #CurrentDomainId is the rail's picker, which is what the
 * legacy Reload read. Prefer the screen's own value -- the operator can change
 * the picker without the list having been re-fetched, and then a reload would
 * quietly swap to a different domain's invitations.
 */
function domainId(): string {
    const scoped = document.getElementById('InviteDomainId') as HTMLInputElement | null;
    if (scoped?.value) return scoped.value;
    return (document.getElementById('CurrentDomainId') as HTMLInputElement | null)?.value || '0';
}

export const List = {
    /** The issue form, scoped to the domain whose invitations are on screen. */
    DisplayIssueModal(): void {
        void ajaxModal({
            modal: MODAL,
            url: `Invite/Issue?domainId=${encodeURIComponent(domainId())}`,
            formId: FORM,
            focusId: 'Email',
            errorTitle: 'Invite Person',
            errorMessage: 'The invite form failed to load:',
        });
    },

    Resend(inviteId: string): void {
        if (!inviteId) return;
        void confirm({
            title: 'Resend Invite',
            html: 'Resend this invite?<br/><br/>'
                + 'The existing link will stop working and a new email will go out.',
            confirmText: 'Resend',
            variant: 'warning',
            onConfirm: () => {
                void ajaxJson({
                    url: `Invite/${inviteId}/Resend`,
                    method: 'POST',
                    alertInto: ALERT_INTO,
                    errorTitle: 'Failed to resend invite',
                    onSuccess: () => {
                        toast('success', 'Invite resent');
                        void List.Reload();
                    },
                    onFailure: data => {
                        toast('danger', 'Failed to resend invite', summaryOf(data));
                        return true;
                    },
                });
            },
        });
    },

    Cancel(inviteId: string): void {
        if (!inviteId) return;
        void confirm({
            title: 'Cancel Invite',
            html: 'Cancel this invite?<br/><br/>'
                + '<span class="text-danger">The existing link stops working immediately '
                + 'and the invitee will need a new invite.</span>',
            confirmText: 'Cancel Invite',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `Invite/${inviteId}`,
                    method: 'DELETE',
                    alertInto: ALERT_INTO,
                    errorTitle: 'Failed to cancel invite',
                    onSuccess: () => {
                        toast('success', 'Invite cancelled');
                        void List.Reload();
                    },
                    onFailure: data => {
                        toast('danger', 'Failed to cancel invite', summaryOf(data));
                        return true;
                    },
                });
            },
        });
    },

    /**
     * Re-fetch the screen. The list is rendered inside Index.cshtml and no route
     * serves the rows on their own, so this swaps the whole view -- a
     * window.location.reload() would land on a layout-less fragment.
     */
    Reload(): Promise<void> {
        return ajaxSwap({
            container: 'ViewContainer',
            url: `Invite/Domain/${domainId()}`,
            alertTitle: 'Failed to reload invitations',
        });
    },
};

export const Issue = {
    SaveClick(): void {
        const form = one(`#${FORM}`) as HTMLFormElement | null;
        if (!form || !isValid(form)) return;
        const busy = (working: boolean): void => {
            setHidden('#InviteIssueContainer .fa-paper-plane', working);
            setHidden('#InviteIssueContainer .fa-spinner', !working);
        };
        busy(true);
        void ajaxJson({
            url: 'Invite/Issue',
            method: 'POST',
            data: serialize(form),
            alertInto: 'InviteIssueMessageContainer',
            errorTitle: 'Failed to send the invite',
            onSuccess: () => {
                hideModal(MODAL);
                toast('success', 'Invite sent');
                // Straight back to the list the modal opened over, with the new
                // invitation on it. The old flow answered on a separate page,
                // so the one thing the operator wanted to see was the one thing
                // they did not get.
                void List.Reload();
            },
            onFailure: data => {
                const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
                if (modelState) setValidationModelState(modelState, '#IssueInviteForm');
                toast('danger', 'Failed to send the invite', summaryOf(data));
                return true;
            },
            onComplete: () => busy(false),
        });
    },
};
